import {
  AfterViewInit,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  NgZone,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { LayoutService } from 'src/app/services/layout/layout.service';
import { CLIENT_NAVIGATIONS, CTA_BUTTON_CHART_CREATE_ID } from './shared/constants';
import { BaseComponent } from 'src/app/components/_base/base.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CONSTANTS } from 'src/app/providers/constants';
import { combineLatest, filter, take } from 'rxjs';
import { Path, RoutePath } from './shared/route';
import { Router, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import { PlanUsage } from './types/plan';
import { PlanService } from 'src/app/services/plan/plan.service';
import { PromoPopupService } from 'src/app/services/promotion/promo-popup.service';
import { DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ENVIRONMENT } from 'src/app/providers/environment';
import { captureException } from '@sentry/angular';
import { LayoutComponent } from 'src/app/components/layout/layout.component';
import { ThemeExtensionBannerComponent } from 'src/app/components/layout/banner/theme-extension-banner/theme-extension-banner.component';
import { BUILD_CONFIG } from 'src/app/providers/environment';
import { ThemeService } from 'src/app/services/theme/theme.service';
import { Client, Roles } from 'src/app/types/identity';
import * as amplitude from '@amplitude/unified';
import { SidebarItem } from './types/general';
import enTranslation from '../assets/i18n/en.json';
import frTranslation from '../assets/i18n/fr.json';
import esTranslation from '../assets/i18n/es.json';
import csTranslation from '../assets/i18n/cs.json';
import daTranslation from '../assets/i18n/da.json';
import deTranslation from '../assets/i18n/de.json';
import itTranslation from '../assets/i18n/it.json';
import nlTranslation from '../assets/i18n/nl.json';
import jaTranslation from '../assets/i18n/ja.json';

const STORE_AMPLITUDE_BLACKLIST = ['bareme-duy-test', 'agoodsize'].map((store) => `${store}.myshopify.com`);

declare global {
  interface Window {
    Cypress: unknown;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    TranslatePipe,
    DecimalPipe,
    MatButtonModule,
    MatProgressSpinnerModule,
    RouterOutlet,
    LayoutComponent,
    ThemeExtensionBannerComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrl: './app.component.scss',
})
export class AppComponent extends BaseComponent implements OnInit, AfterViewInit {
  private readonly layoutService = inject(LayoutService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly planService = inject(PlanService);
  private readonly promoPopupService = inject(PromoPopupService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly env = inject(ENVIRONMENT);
  private readonly themeService = inject(ThemeService);
  private readonly ngZone = inject(NgZone);
  private readonly translate = inject(TranslateService);

  readonly constants = inject(CONSTANTS);

  readonly payingOverUsageCost = signal(false);
  readonly planUsage = signal<PlanUsage | undefined>(undefined);
  readonly hasAuthenticated = signal(false);
  readonly showDashboard = signal(false);
  readonly sidebarItems = signal<SidebarItem[]>([]);
  readonly isAdmin = signal(false);

  readonly TableChartRoute = RoutePath.getPath(Path['table-chart']).fullPath;
  readonly BUILD_CONFIG = BUILD_CONFIG;

  readonly ctaButtonTemplate = viewChild.required<TemplateRef<unknown>>('ctaButtonTemplate');
  // readonly planUsageIndicatorTemplate = viewChild.required<TemplateRef<unknown>>('planUsageIndicatorTemplate');
  readonly aiSizeRecommendationUsageLimitRef = viewChild.required<TemplateRef<unknown>>(
    'aiSizeRecommendationUsageLimitRef',
  );
  readonly overUsageCostRef = viewChild.required<TemplateRef<unknown>>('overUsageCostRef');
  readonly stockNotificationUsageLimitRef = viewChild.required<TemplateRef<unknown>>('stockNotificationUsageLimitRef');

  readonly aiSizeRecommendationPerc = computed(
    () =>
      ((this.planUsage()?.usages.aiSizeRecommendation ?? 0) /
        (this.planUsage()?.activation.planSnapshot.aiSizeRecommendationLimit ?? 1)) *
      100,
  );
  readonly stockNotificationPerc = computed(
    () =>
      ((this.planUsage()?.usages.stockNotification ?? 0) /
        (this.planUsage()?.activation.planSnapshot.stockNotificationLimit ?? 1)) *
      100,
  );
  readonly isOverUsage = computed(() => this.planUsage()?.isOverUsage ?? false);

  constructor() {
    super();

    this.translate.setTranslation('en', enTranslation);
    this.translate.setTranslation('fr', frTranslation);
    this.translate.setTranslation('es', esTranslation);
    this.translate.setTranslation('cs', csTranslation);
    this.translate.setTranslation('da', daTranslation);
    this.translate.setTranslation('de', deTranslation);
    this.translate.setTranslation('it', itTranslation);
    this.translate.setTranslation('nl', nlTranslation);
    this.translate.setTranslation('ja', jaTranslation);
    this.translate.setFallbackLang('en');
    this.translate.use('en');
  }

  ngOnInit() {
    this.sub$ = this.authService.user$.subscribe((user) => {
      this.hasAuthenticated.set(Boolean(user));

      if (user?.theme) this.themeService.setTheme(user.theme);

      if (user && user.role === Roles.Client) {
        if (BUILD_CONFIG === 'production')
          if (!STORE_AMPLITUDE_BLACKLIST.includes(window.shopify.config.shop ?? ''))
            this.ngZone.runOutsideAngular(() => {
              amplitude.initAll(this.env.amplitudeKey, {
                analytics: {
                  serverUrl: `${this.env.server}/ap/al`,
                  autocapture: {
                    attribution: true,
                    fileDownloads: true,
                    formInteractions: true,
                    pageViews: true,
                    sessions: true,
                    elementInteractions: true,
                    networkTracking: true,
                    webVitals: true,
                    frustrationInteractions: true,
                  },
                  userId: window.shopify.config.shop,
                },
                sessionReplay: {
                  serverUrl: this.env.server,
                  configServerUrl: `${this.env.server}/ap/ss-c`,
                  trackServerUrl: `${this.env.server}/ap/ss`,
                  sampleRate: 1,
                },
              });
            });

        this.showDashboard.set(true);
        this.layoutService.ctaButton = {
          id: CTA_BUTTON_CHART_CREATE_ID,
          templateRef: this.ctaButtonTemplate(),
        };

        this.translate.use((user as Client).lang ?? window.shopify?.config.locale ?? 'en');

        // this.layoutService.planUsageIndicatorContent = this.planUsageIndicatorTemplate();
      }

      if (user && user.role === Roles.Admin) {
        this.showDashboard.set(true);
        this.isAdmin.set(true);
        this.layoutService.popCtaButton();
      }
    });

    // this.sub$ = this.authService.reviewPrompt$
    //   .pipe(
    //     filter(() => !this.dialogService.isDialogOpen),
    //     take(1),
    //   )
    //   .subscribe(() => {
    //     this.dialogService.open({
    //       title: 'Share your experience',
    //       content: 'Well done! You created your first chart. We’d love your feedback share a review.',
    //       buttons: [
    //         {
    //           label: "No, I'm having an issue",
    //           onClick: () => {
    //             this.dialogService.close();

    //             this.agentChatStore.setOpen(true);
    //           },
    //           classes: '!border !border-border-6 !font-bold',
    //         },
    //         {
    //           label: 'Leave a review',
    //           onClick: () => {
    //             this.dialogService.close();
    //             window.open(
    //               `https://apps.shopify.com/${this.constants.APP_NAME}#modal-show=WriteReviewModal`,
    //               '_blank',
    //             );
    //           },
    //           color: ButtonColor.primary,
    //         },
    //       ],
    //     });
    //   });

    this.sub$ = this.planService.plan$.subscribe((plan) => {
      this.planUsage.set(plan);

      if (this.env.env !== 'production') {
        if (this.aiSizeRecommendationPerc() >= 75 && !this.isOverUsage())
          this.layoutService.banner = {
            id: this.constants.BANNER_USAGE_LIMITATION as string,
            templateRef: this.aiSizeRecommendationUsageLimitRef(),
            title: this.translate.instant(_('banner.about-your-plan-usage')),
          };

        if (this.stockNotificationPerc() >= 75)
          this.layoutService.banner = {
            id: 'STOCK_NOTIFICATION_USAGE_LIMIT_REACHED',
            templateRef: this.stockNotificationUsageLimitRef(),
            title: this.translate.instant(_('banner.about-your-plan-usage')),
          };

        if (this.isOverUsage())
          this.layoutService.banner = {
            id: this.constants.BANNER_OVERDUE_CHARGE as string,
            templateRef: this.overUsageCostRef(),
            title: this.translate.instant(_('banner.about-your-plan-usage')),
          };
      }
    });

    this.sub$ = combineLatest([
      this.translate.stream(CLIENT_NAVIGATIONS.map((item) => item.name)),
      this.authService.isAdmin$,
    ]).subscribe(([navigationNames, isAdmin]: [Record<string, string>, boolean]) => {
      if (isAdmin) return;

      this.sidebarItems.set(
        CLIENT_NAVIGATIONS.map((item) => ({
          ...item,
          name: navigationNames[item.name] ?? item.name,
        })),
      );
    });
  }

  ngAfterViewInit() {
    this.sub$ = this.authService.user$
      .pipe(
        filter((user) => Boolean(user)),
        take(1),
      )
      .subscribe((user) => {
        if (!user || user.role !== Roles.Client) return;

        this.planService.getCurrentUserPlan().subscribe();
        // A running sale's popup, once per shop (GOO-445).
        this.sub$ = this.promoPopupService.start();
      });
  }

  onNavigateToDashboard() {
    if (this.router.url === '/dashboard') this.layoutService.notifyDashboard$.next();
    else this.router.navigate(RoutePath.getPath(Path['dashboard']).fullPath);
  }

  onPayOverUsageCost() {
    this.payingOverUsageCost.set(true);

    this.planService.payOverUsageCost().subscribe({
      next: ({ link }) => {
        window.open(link, '_top');
      },
      error: (error) => {
        captureException(error);
        this.payingOverUsageCost.set(false);
        this.snackbar.open(
          this.translate.instant(_('setting.failed-to-pay-over-usage-cost')),
          this.translate.instant(_('common.close')),
          {
            panelClass: 'snackbar-error',
          },
        );
      },
    });
  }
}
